import type { FunctionComponent, ReactNode } from 'react';
import { Suspense, lazy } from 'react';

import type { ISingleHistoryBet, ISingleHistoryGame, ISingleHistorySeat } from '../../../../api/fetchers/history/types';
import { CurrencyConnected } from '../../../currency/CurrencyConnected';
import { Loader } from '../../../loader';
import { L12nText } from '../../../text/L12nText';
import { NavigationButton } from '../../footer/HistoryFooter';
import { useHistoryNavigation } from '../../hooks/useHistoryNavigation';

import styles from './HistoryGameInfo.module.css';

const LazyComponent = {
  ANDARBAHAR: lazy(() => import('./results/andarbahar/AndarbaharHistoryResult')),
  BACCARAT: lazy(() => import('./results/baccarat/BaccaratHistoryResult')),
  BINGO: lazy(() => import('./results/bingo/BingoHistoryResult')),
  BLACKJACK: lazy(() => import('./results/blackjack/BlackjackHistoryResult')),
  BOOMORBUST: lazy(() => import('./results/boomorbust/BoomorbustHistoryResult')),
  DRAGONTIGER: lazy(() => import('./results/dragontiger/DragonTigerHistoryResult')),
  TOPCARD: lazy(() => import('./results/topcard/TopCardHistoryResult')),
  MEGAWHEEL: lazy(() => import('./results/megawheel/MegawheelHistoryResult')),
  POWERBALL: lazy(() => import('./results/powerball/PowerballHistoryResult')),
  ROULETTE: lazy(() => import('./results/roulette/RouletteHistoryResult')),
  SICBO: lazy(() => import('./results/sicbo/SicboHistoryResult')),
  SNAKESANDLADDERS: lazy(() => import('./results/snakesandladders/SnakesAndLaddersHistoryResult')),
  SPACEMAN: lazy(() => import('./results/spaceman/SpacemanHistoryResult')),
  SWEETBONANZA: lazy(() => import('./results/sweetbonanza/SweetBonanzaHistoryResult')),
  TREASUREADVENTURE: lazy(() => import('./results/treasureIsland/TreasureIslandHistoryResult')),
  MEGASICBAC: lazy(() => import('./results/megasicbac/MegaSicBacHistoryResult')),
  HIGHFLYER: lazy(() => import('./results/highflyer/HighflyerHistoryResult'))
};

export interface HistoryGameInfoProps {
  type: string;
  game: ISingleHistoryGame;
  seats: ISingleHistorySeat[];
  bets: ISingleHistoryBet[];
  inNavDrawer: boolean;
}

export const HistoryGameInfo: FunctionComponent<HistoryGameInfoProps> = ({
  type,
  game,
  seats,
  bets,
  inNavDrawer,
}: HistoryGameInfoProps) => {
  const { handleNext, handlePrev, isLastGame, isFirstGame, isLoadingDetails } = useHistoryNavigation();

  return (
    <div className={styles.HistoryGameInfo}>
      <div className={`${styles.title} ${!inNavDrawer ? styles.title_centered : ''}`}>
        {inNavDrawer && (
          <NavigationButton onClick={handlePrev} disabled={isFirstGame || isLoadingDetails} iconDirection="left" />
        )}
        <span className={`${inNavDrawer ? styles.gameTitle_drawer : styles.gameTitle}`}>
          <L12nText tk={game.name} />
        </span>
        {inNavDrawer && (
          <NavigationButton onClick={handleNext} disabled={isLastGame || isLoadingDetails} iconDirection="right" />
        )}
      </div>
      <div className={styles.gameInfo}>
        <div className={styles.gameText}>
          <div className={styles.gameTextTitle__small}>{game.date}</div>
        </div>
        <div>
          <div className={styles.gameText}>
            <div className={styles.gameTextTitle}>
              <L12nText tk="BET" />
            </div>
            <div>
              <CurrencyConnected value={game.totalBet} />
            </div>
          </div>
          <div className={styles.gameText}>
            <div className={styles.gameTextTitle}>
              <L12nText tk="NETCASH" />
            </div>
            <div className={game.netAmount > 0 ? styles.win : styles.lose}>
              <CurrencyConnected value={game.netAmount} />
            </div>
          </div>
          <div className={styles.gameText}>
            <div className={styles.gameTextTitle}>
              <L12nText tk="GAMEID" />
            </div>
            <div>#{game.id}</div>
          </div>
          <div className={styles.gameText}>
            <div className={styles.gameTextTitle}>
              <L12nText tk="ROUND_ID" />
            </div>
            <div>#{game.roundId}</div>
          </div>
        </div>
        <div className={styles.gameText}>
          <div className={styles.gameTextTitle}>
            <L12nText tk="DEALER" />: {seats[0]?.name || <L12nText tk="NO_DEALER" />}
          </div>
        </div>
      </div>
      <div className={styles.roundInfo}>
        {!isLoadingDetails && (
          <Suspense fallback={<Loader />}>
            {((type: string, data: ISingleHistoryGame): ReactNode => {
              switch (type) {
                case 'ANDARBAHAR':
                  return <LazyComponent.ANDARBAHAR seats={seats} />;
                case 'BACCARAT':
                  return (
                    <LazyComponent.BACCARAT
                      seats={seats}
                      additional={data.additional.megaBaccaratGameEvents}
                      key={type}
                      tableVariant={game.tableVariant}
                    />
                  );
                case 'BINGO': // Vegas Ball Bonanza
                  return <LazyComponent.BINGO additional={data.additional.bingoGameEvents} />;
                case 'ONEBJ':
                case 'BLACKJACK':
                  return <LazyComponent.BLACKJACK type={type} seats={seats} />;
                case 'BOOMORBUST':
                  return <LazyComponent.BOOMORBUST additional={data.additional.bobDetails} />;
                case 'DRAGONTIGER':
                  return <LazyComponent.DRAGONTIGER result={bets[0].result} seats={seats} />;
                case 'MEGAWHEEL':
                  return (
                    <LazyComponent.MEGAWHEEL
                      result={bets[0].result}
                      additional={data.additional.rngSlots}
                      tableVariant={game.tableVariant ?? ''}
                    />
                  );
                case 'POWERBALL':
                  return <LazyComponent.POWERBALL additional={data.additional.kpbGameDetails} />;
                case 'ROULETTE':
                  return (
                    <LazyComponent.ROULETTE
                      cancelled={data.cancelled}
                      result={bets[0].result}
                      rngSlots={data.additional.rngSlots}
                      rouletteDetails={data.additional.rouletteDetails}
                    />
                  );
                case 'SICBO':
                  return <LazyComponent.SICBO result={bets[0].result} />;
                case 'SNAKESANDLADDERSLIVE':
                  return <LazyComponent.SNAKESANDLADDERS details={data.additional.snlGameDetails} />;
                case 'SPACEMAN':
                  return <LazyComponent.SPACEMAN multiplier={bets[0].result} cancelled={data.cancelled} />;
                case 'SWEETBONANZA':
                  return <LazyComponent.SWEETBONANZA additional={data.additional.sweetbonanza} />;
                case 'TOPCARD':
                  return <LazyComponent.TOPCARD seats={seats} />;
                case 'TREASUREADVENTURE':
                  return <LazyComponent.TREASUREADVENTURE additional={data.additional.taGameSummary} />;
                case 'MEGASICBAC':
                  return <LazyComponent.MEGASICBAC result={bets[0].result} seats={seats} />;
                case 'HIGHFLYER':
                  return <LazyComponent.HIGHFLYER multiplier={bets[0].result} cancelled={data.cancelled} />;
                default:
                  return <div className={styles.roundImage}>Replace me using Instance menu</div>;
              }
            })(type.toUpperCase(), game)}
          </Suspense>
        )}
      </div>
    </div>
  );
};
